import styles from './validate-email.module.scss';
import { useState } from 'react';
import { Col, Row } from 'reactstrap';
import PageTitle from '../page-title/page-title';

import { makeRequest } from '../requestUtils';
import { booleanHeader, Box, CheckboxLabel, Chip, Container, CreateHeaders, dateAndTimeHeader, dateHeader, Divider, HeaderSizeEnum, MultiLineTextField, PrimaryButton, TextField, textHeader } from '@frontend-workspace/react-shared-lib';

import { AccountStatusMessage } from '../helpers';
import { IValidateResult } from '../_models/validate-result';
import { AccountStatusEnum } from '../_enums/account-status';
import { WebkuaTable } from '@frontend-workspace/webkua-lib';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../requestUtils';

/* eslint-disable-next-line */
export interface ValidateEmailProps { }

export function ValidateEmail(props: ValidateEmailProps) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [email, setEmail] = useState<string>('');
  const [validateResult, setValidateResult] = useState<IValidateResult>({
    licenses: [],
    userExistsInNimbus: false,
    companyStoppedInNimbus: false,
    unfinishedStatus: AccountStatusEnum.NoAccountChosen,
    blockedInNimbus: false,
    isActivated: false,
    retiredInWebkua: false,
    blockedInWebkua: false,
    retiredInNimbus: false,
    loans: [],
    personStatus: t("standard.noAccountSelected"),
    accountChanges: []
  });

  const changeEmail = (e: any) => setEmail(e.target.value);

  const validateEmail = () => {
    makeRequest(`admin/validateUserEmail/${email}`)
      .then((result: IValidateResult) => setValidateResult(result));
  };

  const requiredAction = (status: AccountStatusEnum) => {
    switch (status) {
      case AccountStatusEnum.NoAccountOnEmail:
        return t("validateEmail.createAccountOnUser");
      case AccountStatusEnum.NoKnownIssues:
        return t("validateEmail.noActionRequired");
      case AccountStatusEnum.InvitationExists:
        return t("validateEmail.userHasBeenSentAnInvitationByEmailEtc");
      case AccountStatusEnum.AccountRequestExists:
        return t("validateEmail.administratorInCompanyMustApproveAccountRequestEtc");
      case AccountStatusEnum.UserHasNotFinishedRegistration:
        return t("validateEmail.askUserToLoginToMyPageAndCompleteRegistration");
      case AccountStatusEnum.MissingPassword:
        return t("validateEmail.askUserToCreatePasswordFromMyPage");
      case AccountStatusEnum.NoAccountChosen:
        return "";
    };
  };

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}
    >
      <Row>
        <Col>
          <Box component="div" className="pb-5">
            <PageTitle title={t("app.checkFocusAccount")} subtitle={`(${t("standard.status")})`} />
            <Row>
              <Col md='9' xl='6' xxl='4'>
                <div className="d-flex flex-column">
                  <div className="mb-3">
                    <TextField
                      label={t("standard.email")}
                      name='email'
                      value={email}
                      fullWidth
                      onChange={changeEmail}
                      helperText={t("validateEmail.emailYouWantToCheckFocusAccountStatusOn")}
                    />
                  </div>
                  <div>
                    <PrimaryButton
                      onClick={validateEmail}
                      variant='contained'>
                      {t("validateEmail.checkFocusAccountStatus")}
                    </PrimaryButton>
                  </div>
                </div>
              </Col>
            </Row>
            <Divider className="my-4"><Chip label={t("validateEmail.focusAccountStatus")} /></Divider>
            {validateResult && <>
              <Row>
                <Col md='12' xl='6' xxl='6'>
                  <div className='mb-3'>
                    <TextField
                      label={t("validateEmail.statusOnFocusAccount")}
                      value={AccountStatusMessage(validateResult.unfinishedStatus)}
                      fullWidth
                      disabled
                    />
                  </div>
                  <div className='mb-3'>
                    <MultiLineTextField
                      label={t("validateEmail.actionRequired")}
                      value={requiredAction(validateResult.unfinishedStatus)}
                      fullWidth
                      minRows={1}
                      disabled
                    />
                  </div>
                  <div className='mb-3'>
                    <MultiLineTextField
                      label={t("validateEmail.userSupportStatus")}
                      value={validateResult.personStatus}
                      fullWidth
                      minRows={1}
                      disabled
                    />
                  </div>
                </Col>
                <Col md='12' xl='6' xxl='6'>
                  <label>{t("validateEmail.other")}</label>
                  <div className='d-flex flex-row flex-wrap mb-3'>
                    <div>
                      <CheckboxLabel
                        checked={validateResult.isActivated}
                        label={t("validateEmail.userHasLoggedInToMyPage")}
                        disabled
                      />
                      <CheckboxLabel
                        checked={validateResult.blockedInWebkua}
                        label={t("validateEmail.accountBlockedInWebKua")}
                        disabled
                      />
                      <CheckboxLabel
                        checked={validateResult.retiredInWebkua}
                        label={t("validateEmail.personRetiredInWebKua")}
                        disabled
                      />
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col className='mb-3' md='12' xl='6' xxl='4'>
                  <Divider textAlign="left">
                    <label className='mb-3 mb-sm-0'>{t("standard.licenseAccess")}</label>
                  </Divider>
                  <WebkuaTable
                    headers={
                      CreateHeaders([
                        textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM),
                        textHeader("name", t("standard.name"), HeaderSizeEnum.EXTRA_LARGE)
                      ])
                    }
                    data={validateResult.licenses}
                  />
                </Col>
                <Col className='mb-3' md='12' xl='6' xxl='4'>
                  <Divider textAlign="left">
                    <label className='mb-3 mb-sm-0'>{t("validateEmail.accountChanges")}</label>
                  </Divider>
                  <WebkuaTable
                    headers={
                      CreateHeaders([
                        dateAndTimeHeader("activationDate", t("standard.date")),
                        textHeader("companyNumber", t("standard.company"), HeaderSizeEnum.SMALL),
                        textHeader("description", t("standard.description"), HeaderSizeEnum.EXTRA_LARGE),
                        textHeader("byEmail", t("validateEmail.performedBy"), HeaderSizeEnum.SMALL),
                      ])
                    }
                    data={validateResult.accountChanges}
                  />
                </Col>
                <Col className='mb-3' md='12' xl='6' xxl='4'>
                  <Divider textAlign="left">
                    <label className='mb-3 mb-sm-0'>{t("standard.loan")}</label>
                  </Divider>
                  <WebkuaTable
                    headers={CreateHeaders([
                      dateAndTimeHeader("loanCreatedTime", t("standard.date")),
                      textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.SMALL),
                      textHeader("productName", t("standard.product"), HeaderSizeEnum.LARGE),
                      textHeader("productVersion", t("standard.productVersion"), HeaderSizeEnum.SMALL),
                      textHeader("version", t("standard.version"), HeaderSizeEnum.SMALL),
                      booleanHeader("isNewLicensing", t("standard.newLicensingSystem"))
                    ])}
                    data={validateResult.loans}
                  />
                </Col>
              </Row>
            </>}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export default ValidateEmail;
